/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  type BuyBreakTier,
  type ProductBuyBreaksInfo,
  ProductCard,
  UrgentDeliveryMessage,
} from "@farmlands/commerce";
import { type JSX, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { usePermission } from "../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { useProductDetails } from "../../products/api/hooks/useProductDetails";
import { ProductVariantDetails } from "../../products/components/ProductCard/ProductVariantDetails";
import { ProductType } from "../../products/components/ProductCard/types";
import {
  EProductLocation,
  openProductDetailsLink,
} from "../../products/components/ProductDetails/utils/openProductDetails";
import { usePriceAndAvailability } from "../../products/hooks/usePriceAndAvailability";
import { mapItemToCardProduct, PRODUCT_CARD_PLACEHOLDER } from "../../products/utils/card-product";
import { getPromotionBadges } from "../../products/utils/getPromotionBadges";
import { toProductBuyBreaksInfo } from "../../products/utils/to-buy-break";
import { useProductStatus } from "../../products/utils/useProductStatus";
import { useCartItemActions } from "../hooks/useCartItemActions";
import { selectCurrentCart } from "../storage/cart.slice";
import { CartItem } from "../storage/cart.types";
import { getUrgentDeliveryUnavailable } from "../utils/getUrgentDeliveryUnavailable";

export interface CartProductCardProps {
  className?: string;
  item: CartItem;
  onChangeQuantity?: (value: number) => void;
  handleOpenEditPriceModal?: () => void;
  priceEditable?: boolean;
}

export const CartProductCard = ({
  className,
  item,
  onChangeQuantity,
  handleOpenEditPriceModal,
  priceEditable = false,
}: CartProductCardProps): JSX.Element => {
  const navigate = useNavigate();
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const cart = useSelector(selectCurrentCart);
  const remoteConfig = useSelector(selectRemoteConfig);
  const canEditPrice = usePermission("StaffSpecific_Pricing_PriceEdit").allowed;

  const { removeItem } = useCartItemActions(item);

  const isTfo = currentAccount?.hasTfoAccess;
  const { noPriceAvailable } = useProductStatus(item.lifeCycleState, item.basePriceExcGst);

  const { price, stock, lifecycleNotice } = usePriceAndAvailability({
    item,
    isTfo,
    homeBranchId: currentAccount?.homeBranchId,
    priceQuantity: item.quantity,
    noPriceAvailable,
    stockAvailabilities: item.stockAvailabilities ?? [],
    cart,
  });

  const showUrgentDeliveryUnavailable = useMemo((): boolean => {
    if (!cart || !currentAccount) {
      return false;
    }

    return getUrgentDeliveryUnavailable({
      cart,
      productId: item.productId,
      stockAvailabilities: item.stockAvailabilities,
      homeBranchId: currentAccount.homeBranchId,
    });
  }, [cart, currentAccount, item.productId, item.stockAvailabilities]);

  const onTitleClick = useCallback((): void => {
    openProductDetailsLink(item, false, ProductType.All, EProductLocation.CART, navigate);
  }, [item, navigate]);

  const canEdit: boolean = canEditPrice && priceEditable && remoteConfig.discountEnabled;

  const hasBuyBreaks = Boolean(item.priceBreaks?.length);
  const hasEditedPrice = Boolean(item.editedPriceExcGst && item.editedPriceExcGst > 0);
  const shouldFetchBuyBreaks = remoteConfig.buyBreaksEnabled && hasBuyBreaks && !hasEditedPrice;

  const { data: productDetails } = useProductDetails(
    item.masterProductId || item.productId,
    shouldFetchBuyBreaks
  );

  const buyBreaks: ProductBuyBreaksInfo | undefined = useMemo(
    (): ProductBuyBreaksInfo | undefined =>
      shouldFetchBuyBreaks ? toProductBuyBreaksInfo(productDetails?.priceBreaks) : undefined,
    [productDetails?.priceBreaks, shouldFetchBuyBreaks]
  );

  const onBuyBreakTierSelect = useCallback(
    (tier: BuyBreakTier): void => {
      onChangeQuantity?.(tier.from);
    },
    [onChangeQuantity]
  );

  const promotionBadges = useMemo(
    (): string[] =>
      getPromotionBadges(
        item.promotions,
        item.priceBreaks,
        remoteConfig.traderDealsFilterValue,
        remoteConfig.traderDealsEnabled,
        shouldFetchBuyBreaks
      ).map((badge) => badge.label),
    [
      item.promotions,
      item.priceBreaks,
      remoteConfig.traderDealsFilterValue,
      remoteConfig.traderDealsEnabled,
      shouldFetchBuyBreaks,
    ]
  );

  const variants = useMemo(
    (): JSX.Element => <ProductVariantDetails item={item} enableVariants={false} />,
    [item]
  );

  return (
    <ProductCard
      className={className}
      onTitleClick={onTitleClick}
      product={mapItemToCardProduct(item)}
      placeholderSrc={
        remoteConfig.enableShopProductDetailsPage ? undefined : PRODUCT_CARD_PLACEHOLDER
      }
      price={price}
      stock={stock}
      statusBadge={lifecycleNotice}
      promotionBadges={promotionBadges}
      buyBreaks={buyBreaks}
      onBuyBreakTierSelect={buyBreaks ? onBuyBreakTierSelect : undefined}
      variants={variants}
      discountBanner={item.discounts?.length ? "Special offer!" : undefined}
      warning={showUrgentDeliveryUnavailable ? <UrgentDeliveryMessage /> : undefined}
      quantity={{ value: item.quantity, onChange: (value) => onChangeQuantity?.(value) }}
      onRemove={removeItem}
      onEditPrice={canEdit ? handleOpenEditPriceModal : undefined}
      editPriceLabel="Edit price"
    />
  );
};
